import { type FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchTextField } from "@packsize/components";
import type { Role, User } from "@packsize/identity-api-sdk";
import {
	Card,
	Grid,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from "@mui/material";

import { FailedRetry } from "@/components/failed-retry";
import { Loading } from "@/components/loading";
import { search } from "@/utils/search";
import { ConfigurationPageLayout } from "@/views/configuration/components";

import { DeleteUserButton } from "./delete-user-button";
import { EditUserButton } from "./edit-user-button";
import { NewUserButton } from "./new-user-button";
import { getUserRoles } from "./users.utils";

interface UsersLegacyTemplateProps {
	readonly users: User[];
	readonly roles: Role[];
	readonly isLoading?: boolean;
	readonly isError?: boolean;
	readonly onRetry?: () => void | Promise<unknown>;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export const UsersLegacyTemplate: FC<UsersLegacyTemplateProps> = ({
	users,
	roles,
	isLoading = false,
	isError = false,
	onRetry,
}) => {
	const intl = useIntl();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [searchTerm, setSearchTerm] = useState("");
	const filteredUsers =
		searchTerm.length < 3 ? users : search(searchTerm, users, ["userName", "firstName", "lastName"]);

	const actions = (
		<>
			<SearchTextField
				value={searchTerm}
				onChange={(value) => {
					setSearchTerm(value);
					setPage(0);
				}}
				placeholder={intl.formatMessage({ id: "Search users...", defaultMessage: "Search users..." })}
				data-testid="users__search__users"
				sx={{ backgroundColor: "common.white", width: "300px" }}
				size="small"
			/>
			<NewUserButton />
		</>
	);

	return (
		<ConfigurationPageLayout title={intl.formatMessage({ id: "Users", defaultMessage: "Users" })} actions={actions}>
			<Card component={Grid} container direction="column">
				{isLoading ? (
					<Loading />
				) : isError ? (
					<FailedRetry retry={onRetry} />
				) : (
					<>
						<TableContainer sx={{ flex: 1 }}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>
											<FormattedMessage id="Username" defaultMessage="Username" />
										</TableCell>
										<TableCell>
											<FormattedMessage id="First Name" defaultMessage="First Name" />
										</TableCell>
										<TableCell>
											<FormattedMessage id="Last Name" defaultMessage="Last Name" />
										</TableCell>
										<TableCell>
											<FormattedMessage id="Roles" defaultMessage="Roles" />
										</TableCell>
										<TableCell align="center">
											<FormattedMessage id="Edit" defaultMessage="Edit" />
										</TableCell>
										<TableCell align="center">
											<FormattedMessage id="Delete" defaultMessage="Delete" />
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
										<StyledTableRow key={item.id}>
											<TableCell sx={{ fontWeight: 500 }}>{item.userName}</TableCell>
											<TableCell>{item.firstName}</TableCell>
											<TableCell>{item.lastName}</TableCell>
											<TableCell>
												{getUserRoles(item, roles)
													.map((role) => role.name)
													.join(", ")}
											</TableCell>
											<TableCell align="center">
												<EditUserButton user={item} />
											</TableCell>
											<TableCell align="center">
												<DeleteUserButton user={item} />
											</TableCell>
										</StyledTableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[50, 100, 500]}
							component="div"
							count={filteredUsers.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={(_, pageNumber) => setPage(pageNumber)}
							onRowsPerPageChange={(event) => {
								setRowsPerPage(parseInt(event.target.value));
								setPage(0);
							}}
						/>
					</>
				)}
			</Card>
		</ConfigurationPageLayout>
	);
};
